import LayoutHR from "layouts/layout-hr";
import React, { useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import Select from "react-select";
import { useNavigate } from "react-router-dom";
import { useCanvasHandler } from "hooks/useCanvasHandler";
import CreateSalaryPage from "./components/create-salary-page";
import SalaryDetailPage from "./components/salary-detail-page";
import SalaryDetailManagement from "./components/salary-detail-management";
import SalarySummaryReport from "./components/salary-summary-report";
import SalarySlipExport, { getSalarySlipStyles, getSalarySlipStylesV2, getSalarySlipHtmlContent, isSalarySlipLong } from "./components/salary-slip-export";
import { getDevelopmentBanner, DEVELOPMENT_STATUS } from "utils/devNotice";
import { useSalary } from "./context/SalaryContext";
import { SalaryDetailProvider } from "./context/SalaryDetailContext";
import { UserProvider, useUser } from "contexts/v1/masterData/UserContext";
import PrintJson from "utils/printJson";
import MasterDataFormV2 from "../MasterDataFormV2";
import { getRequest, patchRequest, postRequest } from "services/api";
import { URLS } from "utils/constants";
import "./salary-page.css";
import { getFacilityTotal } from "./utils/salaryHelpers";

const formatPayrollPeriod = (period = {}) => {
  if (!period.startDate || !period.endDate) return "";
  const formatter = new Intl.DateTimeFormat("id-ID", { day: "2-digit", month: "short", year: "numeric" });
  return `${formatter.format(new Date(period.startDate))} - ${formatter.format(new Date(period.endDate))}`;
};

const toDateKey = (date) => [
  date.getFullYear(),
  String(date.getMonth() + 1).padStart(2, "0"),
  String(date.getDate()).padStart(2, "0"),
].join("-");

const formatPayloadDate = (value) => {
  if (!value) return null;
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return toDateKey(date);
};

const getSalaryPeriod = (dateValue) => {
  const date = dateValue ? new Date(dateValue) : new Date();
  const isMonthMarker = date.getDate() <= 25;
  const start = isMonthMarker
    ? new Date(date.getFullYear(), date.getMonth() - 1, 26)
    : new Date(date.getFullYear(), date.getMonth(), 26);
  const end = isMonthMarker
    ? new Date(date.getFullYear(), date.getMonth(), 25)
    : new Date(date.getFullYear(), date.getMonth() + 1, 25);
  const monthLabel = end.toLocaleDateString("id-ID", { month: "long", year: "numeric" });
  return {
    key: toDateKey(start),
    label: monthLabel,
    range: formatPayrollPeriod({ startDate: toDateKey(start), endDate: toDateKey(end) }),
  };
};

const getSalaryDepartmentName = (salary = {}, usersById = new Map()) => {
  const sourceUser = usersById.get(salary.user_id || salary.user?.id) || {};
  return salary.department_name
  || salary.user?.department?.name
  || salary.department?.name
  || sourceUser.department?.name
  || salary.user?.department_name
  || sourceUser.department_name
  || "Tanpa Departemen";
};

const salaryEmailPlaceholders = [
  "{{staff_name}}",
  "{{department_name}}",
  "{{position_name}}",
  "{{email}}",
  "{{rekening}}",
  "{{salary_period}}",
  "{{payroll_start_date}}",
  "{{payroll_end_date}}",
  "{{basic_salary}}",
  "{{total_salary}}",
  "{{total_work}}",
];

const getSalaryEmailSubject = () => "Slip Gaji {{salary_period}} - {{staff_name}}";

const getSalaryEmailBody = () => `Halo {{staff_name}},

Berikut kami kirimkan Slip Gaji periode {{salary_period}}.

Ringkasan:
Nama: {{staff_name}}
Departemen: {{department_name}}
Jabatan: {{position_name}}
Total hari kerja: {{total_work}}
Total gaji bersih: {{total_salary}}

Detail lengkap tersedia pada file PDF Salary Details yang terlampir.

Terima kasih.`;

const getBatchStatusClass = (status) => {
  const normalized = String(status || "").toLowerCase();
  if (normalized === "sent" || normalized === "completed" || normalized === "success") return "bg-success-lt text-success";
  if (normalized === "failed") return "bg-red-lt text-red";
  if (normalized === "processing" || normalized === "pending" || normalized === "queued") return "bg-warning-lt text-warning";
  return "bg-secondary-lt text-secondary";
};

const formatCurrencyText = (amount) => new Intl.NumberFormat("id-ID", {
  style: "currency",
  currency: "IDR",
  minimumFractionDigits: 0,
}).format(Number(amount || 0));

const getGenerateSalarySummary = (response = {}) => {
  const rows = Array.isArray(response.data) ? response.data : [];
  const overDoRows = rows.filter((row) => Number(row.over_do_days || 0) > 0 || Number(row.over_do_deduction || 0) > 0);
  const totalOverDoDays = overDoRows.reduce((total, row) => total + Number(row.over_do_days || 0), 0);
  const totalOverDoDeduction = overDoRows.reduce((total, row) => total + Number(row.over_do_deduction || 0), 0);
  const totalMorningMealDays = rows.reduce((total, row) => total + Number(row.morning_meal_days || 0), 0);
  const totalMorningMeal = rows.reduce((total, row) => total + Number(row.morning_meal_total || 0), 0);
  const totalSalary = rows.reduce((total, row) => total + Number(row.total_salary || 0), 0);

  return {
    rows,
    overDoRows,
    totalOverDoDays,
    totalOverDoDeduction,
    totalMorningMealDays,
    totalMorningMeal,
    totalSalary,
    payrollStartDate: response.payroll_start_date,
    payrollEndDate: response.payroll_end_date,
  };
};

const SalaryEmailSettingsCanvas = ({
  mode,
  targetSalaryIds,
  targetSalaries,
  payrollPeriod,
  filterOptions,
}) => {
  const periodLabel = formatPayrollPeriod(payrollPeriod) || "periode salary terpilih";
  const [subject, setSubject] = useState(getSalaryEmailSubject());
  const [body, setBody] = useState(getSalaryEmailBody());
  const [attachSalaryDetailPdf, setAttachSalaryDetailPdf] = useState(true);
  const [pdfTemplate, setPdfTemplate] = useState("salary_details");
  const [placeholders, setPlaceholders] = useState(salaryEmailPlaceholders);
  const [templateLoading, setTemplateLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState("");
  const [batchResult, setBatchResult] = useState(null);
  const [batchHistory, setBatchHistory] = useState({ data: [], pagination: null, loading: false });
  const [activeEmailTab, setActiveEmailTab] = useState("send");
  const recipientCount = targetSalaries.filter((salary) => salary.user?.email).length;
  const missingEmailCount = targetSalaries.length - recipientCount;

  const loadBatchHistory = async () => {
    setBatchHistory((current) => ({ ...current, loading: true }));
    try {
      const response = await getRequest("/ahr/salaries/send-email-batch?limit=10&orderBy=created_at&sort=desc");
      if (!response?.success) throw new Error(response?.message || "Gagal mengambil riwayat batch email.");
      setBatchHistory({
        data: Array.isArray(response.data) ? response.data : [],
        pagination: response.pagination || null,
        loading: false,
      });
    } catch (error) {
      setBatchHistory((current) => ({ ...current, loading: false }));
      Swal.fire("Riwayat email gagal dimuat", error.message || "Silakan coba lagi.", "warning");
    }
  };

  useEffect(() => {
    let mounted = true;
    const loadEmailTemplate = async () => {
      setTemplateLoading(true);
      try {
        const [templateResponse, placeholderResponse] = await Promise.all([
          getRequest("/ahr/salaries/email-template"),
          getRequest("/ahr/salaries/email-placeholders").catch(() => null),
        ]);

        if (!mounted) return;
        const template = templateResponse?.data || {};
        setSubject(template.subject || getSalaryEmailSubject());
        setBody(template.body || getSalaryEmailBody());
        setAttachSalaryDetailPdf(Boolean(template.attach_salary_detail_pdf ?? true));
        setPdfTemplate(template.pdf_template || "salary_details");

        const placeholderData = placeholderResponse?.data;
        if (Array.isArray(placeholderData)) {
          setPlaceholders(placeholderData);
        } else if (Array.isArray(placeholderData?.placeholders)) {
          setPlaceholders(placeholderData.placeholders);
        }
      } catch (error) {
        if (mounted) {
          Swal.fire("Template email gagal dimuat", error.message || "Silakan coba lagi.", "warning");
        }
      } finally {
        if (mounted) setTemplateLoading(false);
      }
    };

    loadEmailTemplate();
    loadBatchHistory();
    return () => {
      mounted = false;
    };
  }, []);

  const buildPayload = (dryRun = false) => ({
    salary_ids: targetSalaryIds,
    payroll_start_date: formatPayloadDate(payrollPeriod.startDate),
    payroll_end_date: formatPayloadDate(payrollPeriod.endDate),
    subject,
    body,
    attach_salary_detail_pdf: attachSalaryDetailPdf,
    pdf_template: pdfTemplate,
    dry_run: dryRun,
  });

  const getBatchId = (response) => response?.data?.batch_id || response?.data?.id || response?.data?.batch?.id;

  const handleSaveTemplate = async () => {
    setActionLoading("template");
    try {
      const response = await patchRequest("/ahr/salaries/email-template", {
        subject,
        body,
        attach_salary_detail_pdf: attachSalaryDetailPdf,
        pdf_template: pdfTemplate,
      });
      if (!response?.success) throw new Error(response?.message || "Gagal menyimpan template email.");
      Swal.fire("Berhasil", response.message || "Template email berhasil disimpan.", "success");
    } catch (error) {
      Swal.fire("Gagal", error.message || "Template email gagal disimpan.", "error");
    } finally {
      setActionLoading("");
    }
  };

  const handlePollStatus = async (batchId = getBatchId(batchResult)) => {
    if (!batchId) return;
    setActionLoading("poll");
    try {
      const response = await getRequest(`/ahr/salaries/send-email-batch/${batchId}`);
      if (!response?.success) throw new Error(response?.message || "Gagal mengambil status batch.");
      setBatchResult(response);
      setActiveEmailTab("history");
    } catch (error) {
      Swal.fire("Gagal", error.message || "Status batch gagal dimuat.", "error");
    } finally {
      setActionLoading("");
    }
  };

  const handleSendBatch = async (dryRun = false) => {
    const confirmText = dryRun
      ? "Validasi payload email tanpa mengirim email?"
      : `Kirim slip gaji ke ${targetSalaryIds.length} data salary?`;
    const result = await Swal.fire({
      title: dryRun ? "Dry Run Email Salary" : "Kirim Email Salary",
      text: confirmText,
      icon: dryRun ? "info" : "warning",
      showCancelButton: true,
      confirmButtonText: dryRun ? "Ya, dry run" : "Ya, kirim",
      cancelButtonText: "Batal",
    });

    if (!result.isConfirmed) return;

    setActionLoading(dryRun ? "dry-run" : "send");
    try {
      const response = await postRequest("/ahr/salaries/send-email-batch", buildPayload(dryRun));
      if (!response?.success) throw new Error(response?.message || "Gagal mengirim email salary.");
      setBatchResult(response);

      const batchId = getBatchId(response);
      Swal.fire("Berhasil", response.message || (dryRun ? "Dry run berhasil." : "Batch email berhasil dibuat."), "success");
      loadBatchHistory();
      if (batchId && !dryRun) {
        setTimeout(() => handlePollStatus(batchId), 1000);
      }
    } catch (error) {
      Swal.fire("Gagal", error.message || "Email salary gagal diproses.", "error");
    } finally {
      setActionLoading("");
    }
  };

  const apiPayloadPreview = buildPayload(true);
  const currentBatchId = getBatchId(batchResult);
  const batchDetail = batchResult?.data || null;
  const batchItems = Array.isArray(batchDetail?.items) ? batchDetail.items : [];
  const tabItems = [
    { key: "setup", label: "Setup" },
    { key: "send", label: "Send" },
    { key: "history", label: "History" },
  ];

  return (
    <div className="salary-email-settings-canvas">
      <div className="salary-email-shell">
        <div>
          <h3 className="mb-1">Batch Salary Email Settings</h3>
          <div className="text-muted small">
            Email dikirim oleh API per staff dengan attachment PDF Salary Details. Periode payroll hanya untuk placeholder, bukan filter data salary.
          </div>
        </div>
        <div className="salary-email-tab-list">
          {tabItems.map((tab) => (
            <button
              key={tab.key}
              type="button"
              className={`salary-email-tab ${activeEmailTab === tab.key ? "active" : ""}`}
              onClick={() => setActiveEmailTab(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {activeEmailTab === "setup" && (
        <div className="row g-3">
          <div className="col-lg-8">
            <div className="card">
              <div className="card-header py-2 d-flex align-items-center justify-content-between">
                <h3 className="card-title mb-0">Email Template</h3>
                <button className="btn btn-outline-primary btn-sm" onClick={handleSaveTemplate} disabled={Boolean(actionLoading) || templateLoading}>
                  {actionLoading === "template" ? "Saving..." : "Save Template"}
                </button>
              </div>
              <div className="card-body">
                {templateLoading && <div className="alert alert-light py-2">Loading template email...</div>}
                <div className="mb-3">
                  <label className="form-label">Subject</label>
                  <input className="form-control" value={subject} onChange={(event) => setSubject(event.target.value)} />
                </div>
                <div className="mb-3">
                  <label className="form-label">Body Text</label>
                  <textarea className="form-control salary-email-body" value={body} onChange={(event) => setBody(event.target.value)} rows={14} />
                </div>
                <div className="row g-3 align-items-end">
                  <div className="col-md-7">
                    <label className="form-label">PDF Template</label>
                    <input className="form-control" value={pdfTemplate} onChange={(event) => setPdfTemplate(event.target.value)} />
                  </div>
                  <div className="col-md-5">
                    <div className="form-check form-switch salary-email-switch">
                      <input className="form-check-input" type="checkbox" checked={attachSalaryDetailPdf} onChange={(event) => setAttachSalaryDetailPdf(event.target.checked)} />
                      <label className="form-check-label">Attach PDF Salary Details</label>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-lg-4">
            <div className="card">
              <div className="card-header py-2"><h3 className="card-title mb-0">Placeholder</h3></div>
              <div className="card-body">
                <div className="salary-email-placeholder-list">
                  {placeholders.map((placeholder) => (
                    <code key={typeof placeholder === "string" ? placeholder : placeholder.key || placeholder.name}>
                      {typeof placeholder === "string" ? placeholder : placeholder.key || placeholder.name}
                    </code>
                  ))}
                </div>
              </div>
            </div>
            <div className="alert alert-light mt-3 mb-0">
              <strong>Catatan:</strong> Template ini dipakai untuk batch dan single staff. Backend yang mengganti placeholder per staff.
            </div>
          </div>
        </div>
      )}

      {activeEmailTab === "send" && (
        <div className="row g-3">
          <div className="col-lg-5">
            <div className="card">
              <div className="card-header py-2"><h3 className="card-title mb-0">Target Pengiriman</h3></div>
              <div className="card-body">
                <div className="row g-2 mb-3">
                  <div className="col-6"><div className="salary-email-stat"><span>Mode</span><strong>{mode === "single" ? "Single Staff" : "Batch"}</strong></div></div>
                  <div className="col-6"><div className="salary-email-stat"><span>Periode</span><strong>{periodLabel}</strong></div></div>
                  <div className="col-6"><div className="salary-email-stat"><span>Salary Data</span><strong>{targetSalaryIds.length}</strong></div></div>
                  <div className="col-6"><div className="salary-email-stat"><span>Email Valid</span><strong>{recipientCount}</strong></div></div>
                </div>
                {filterOptions.filterMonth && filterOptions.filterYear && (
                  <div className="alert alert-light py-2">Payroll Month/Year aktif: {filterOptions.filterMonth}/{filterOptions.filterYear}. Data yang dikirim tetap berdasarkan salary_ids terpilih.</div>
                )}
                {missingEmailCount > 0 && (
                  <div className="alert alert-warning py-2">{missingEmailCount} staff belum punya email dan akan dikembalikan sebagai skipped/failed oleh BE.</div>
                )}
                <div className="salary-email-recipient-list">
                  {targetSalaries.map((salary) => (
                    <div className="salary-email-recipient" key={salary.id}>
                      <strong>{salary.user?.name || salary.user_id}</strong>
                      <span>{salary.user?.email || "Email belum ada"}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
          <div className="col-lg-7">
            <div className="card mb-3">
              <div className="card-header py-2 d-flex align-items-center justify-content-between">
                <h3 className="card-title mb-0">Send Action</h3>
                <button className="btn btn-link btn-sm" onClick={() => setActiveEmailTab("setup")}>Edit Template</button>
              </div>
              <div className="card-body">
                <div className="salary-email-send-summary mb-3">
                  <div><span>Subject</span><strong>{subject || "-"}</strong></div>
                  <div><span>Attachment</span><strong>{attachSalaryDetailPdf ? pdfTemplate : "Tidak dilampirkan"}</strong></div>
                </div>
                <div className="d-flex flex-wrap gap-2">
                  <button className="btn btn-outline-info" onClick={() => handleSendBatch(true)} disabled={Boolean(actionLoading) || templateLoading}>{actionLoading === "dry-run" ? "Checking..." : "Dry Run"}</button>
                  <button className="btn btn-primary" onClick={() => handleSendBatch(false)} disabled={Boolean(actionLoading) || templateLoading || targetSalaryIds.length === 0}>{actionLoading === "send" ? "Sending..." : "Send Email"}</button>
                  {currentBatchId && <button className="btn btn-outline-secondary" onClick={() => handlePollStatus(currentBatchId)} disabled={Boolean(actionLoading)}>{actionLoading === "poll" ? "Refreshing..." : "Refresh Status"}</button>}
                </div>
              </div>
            </div>
            <div className="card">
              <div className="card-header py-2"><h3 className="card-title mb-0">{batchResult ? "Result / Status" : "Payload Preview"}</h3></div>
              <div className="card-body">
                <pre className="salary-email-api-draft">{batchResult ? JSON.stringify(batchResult, null, 2) : JSON.stringify(apiPayloadPreview, null, 2)}</pre>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeEmailTab === "history" && (
        <div className="row g-3">
          <div className="col-lg-5">
            <div className="card">
              <div className="card-header py-2 d-flex align-items-center justify-content-between">
                <h3 className="card-title mb-0">Riwayat Email</h3>
                <button className="btn btn-outline-secondary btn-sm" onClick={loadBatchHistory} disabled={batchHistory.loading || Boolean(actionLoading)}>{batchHistory.loading ? "Loading..." : "Refresh"}</button>
              </div>
              <div className="card-body p-0">
                {batchHistory.data.length > 0 ? (
                  <div className="salary-email-history-list">
                    {batchHistory.data.map((batch) => (
                      <div className="salary-email-history-item" key={batch.id}>
                        <div className="d-flex align-items-start justify-content-between gap-2">
                          <div><strong>{batch.subject || "Batch Email Salary"}</strong><div className="text-muted small">{batch.payroll_start_date || "-"} - {batch.payroll_end_date || "-"}</div></div>
                          <span className={`badge ${getBatchStatusClass(batch.status)}`}>{batch.status || "-"}</span>
                        </div>
                        <div className="salary-email-history-meta"><span>Req: {batch.total_requested ?? 0}</span><span>Sent: {batch.total_sent ?? 0}</span><span>Failed: {batch.total_failed ?? 0}</span></div>
                        <div className="d-flex align-items-center justify-content-between mt-2">
                          <span className="text-muted small">{batch.created_at || batch.started_at || "-"}</span>
                          <button className="btn btn-outline-primary btn-sm" onClick={() => handlePollStatus(batch.id)} disabled={Boolean(actionLoading)}>View</button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-muted small p-3">{batchHistory.loading ? "Loading riwayat email..." : "Belum ada riwayat pengiriman."}</div>
                )}
              </div>
            </div>
          </div>
          <div className="col-lg-7">
            <div className="card">
              <div className="card-header py-2 d-flex align-items-center justify-content-between">
                <h3 className="card-title mb-0">Batch Detail</h3>
                {currentBatchId && <span className={`badge ${getBatchStatusClass(batchDetail?.status || batchResult?.summary?.status)}`}>{batchDetail?.status || batchResult?.summary?.status || "-"}</span>}
              </div>
              <div className="card-body">
                {batchDetail ? (
                  <>
                    <div className="row g-2 mb-3">
                      <div className="col-md-4"><div className="salary-email-stat"><span>Requested</span><strong>{batchDetail.total_requested ?? batchResult?.summary?.total_requested ?? 0}</strong></div></div>
                      <div className="col-md-4"><div className="salary-email-stat"><span>Sent</span><strong>{batchDetail.total_sent ?? batchResult?.summary?.total_sent ?? 0}</strong></div></div>
                      <div className="col-md-4"><div className="salary-email-stat"><span>Failed</span><strong>{batchDetail.total_failed ?? batchResult?.summary?.total_failed ?? 0}</strong></div></div>
                    </div>
                    {batchItems.length > 0 ? (
                      <div className="salary-email-item-list">
                        {batchItems.map((item) => (
                          <div className="salary-email-item" key={item.id || `${item.salary_id}-${item.email}`}>
                            <div><strong>{item.staff_name || item.user_id || "-"}</strong><span>{item.email || "-"}</span></div>
                            <div className="text-end"><span className={`badge ${getBatchStatusClass(item.status)}`}>{item.status || "-"}</span>{item.message && <small>{item.message}</small>}</div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <pre className="salary-email-api-draft">{JSON.stringify(batchResult, null, 2)}</pre>
                    )}
                  </>
                ) : (
                  <div className="text-muted small">Pilih salah satu riwayat batch untuk melihat detail item pengiriman.</div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const SalaryPage = () => {
  const navigate = useNavigate();
  const { users } = useUser();
  const {
    salaries,
    filterOptions,
    Pagination,
    refetch,
    generateSalary,
    fetchAllSalaries,
    payrollPeriod,
    handleMonthFilterChange,
    handleYearFilterChange,
    handleUserFilterChange,
    handleDepartmentFilterChange,
    deleteSalary,
    handlePerPageChange,
  } = useSalary();

  const { data: salaryData, pagination, loading } = salaries;
  const [selectedSalaryIds, setSelectedSalaryIds] = useState([]);
  const usersById = useMemo(() => new Map((users.data || []).map((user) => [user.id, user])), [users.data]);
  const departmentFilterOptions = useMemo(() => {
    const departments = new Map();
    (users.data || []).forEach((user) => {
      const departmentId = user.department_id || user.department?.id;
      const departmentName = user.department?.name || user.department_name;
      if (departmentId && departmentName) departments.set(departmentId, departmentName);
    });

    return [
      { value: "", label: "All Departments" },
      ...Array.from(departments.entries())
        .map(([value, label]) => ({ value, label }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    ];
  }, [users.data]);
  const userFilterOptions = useMemo(() => {
    const grouped = new Map();
    (users.data || [])
      .filter((user) => (
        !filterOptions.filterDepartmentId
        || String(user.department_id || user.department?.id) === String(filterOptions.filterDepartmentId)
      ))
      .forEach((user) => {
      const departmentName = user.department?.name || user.department_name || "Tanpa Departemen";
      if (!grouped.has(departmentName)) grouped.set(departmentName, []);
      grouped.get(departmentName).push({
        value: user.id,
        label: user.name,
        departmentName,
      });
    });

    return Array.from(grouped.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([departmentName, options]) => ({
        label: departmentName,
        options: options.sort((a, b) => a.label.localeCompare(b.label)),
      }));
  }, [filterOptions.filterDepartmentId, users.data]);
  const selectedDepartmentOption = useMemo(() => (
    departmentFilterOptions.find((option) => String(option.value) === String(filterOptions.filterDepartmentId)) || departmentFilterOptions[0]
  ), [departmentFilterOptions, filterOptions.filterDepartmentId]);
  const selectedUserOption = useMemo(() => (
    userFilterOptions
      .flatMap((group) => group.options)
      .find((option) => option.value === filterOptions.filterUserId) || null
  ), [filterOptions.filterUserId, userFilterOptions]);
  const visibleSalaryIds = useMemo(() => (salaryData || []).map((salary) => salary.id), [salaryData]);
  const selectedVisibleCount = visibleSalaryIds.filter((id) => selectedSalaryIds.includes(id)).length;
  const isAllVisibleSelected = visibleSalaryIds.length > 0 && selectedVisibleCount === visibleSalaryIds.length;
  const groupedSalaryData = useMemo(() => {
    const groups = new Map();
    let rowNumber = pagination?.from || 1;
    (salaryData || []).forEach((salary) => {
      const period = getSalaryPeriod(salary.date_salary);
      if (!groups.has(period.key)) {
        groups.set(period.key, {
          ...period,
          departmentMap: new Map(),
          rowCount: 0,
        });
      }

      const group = groups.get(period.key);
      const departmentName = getSalaryDepartmentName(salary, usersById);
      if (!group.departmentMap.has(departmentName)) {
        group.departmentMap.set(departmentName, {
          name: departmentName,
          rows: [],
        });
      }

      group.departmentMap.get(departmentName).rows.push({ ...salary, displayNumber: rowNumber });
      group.rowCount += 1;
      rowNumber += 1;
    });
    return Array.from(groups.values())
      .map((group) => ({
        ...group,
        departments: Array.from(group.departmentMap.values())
          .sort((a, b) => a.name.localeCompare(b.name)),
      }))
      .sort((a, b) => b.key.localeCompare(a.key));
  }, [pagination?.from, salaryData, usersById]);

  useEffect(() => {
    setSelectedSalaryIds([]);
  }, [salaryData]);

  const handleToggleSalarySelection = (id) => {
    setSelectedSalaryIds((prev) => (
      prev.includes(id) ? prev.filter((selectedId) => selectedId !== id) : [...prev, id]
    ));
  };

  const handleToggleSelectVisible = () => {
    setSelectedSalaryIds((prev) => {
      const withoutVisible = prev.filter((id) => !visibleSalaryIds.includes(id));
      return isAllVisibleSelected ? withoutVisible : [...withoutVisible, ...visibleSalaryIds];
    });
  };

  const {
    // handleSuccess,
    handleError,
    showCanvasHandler,
    handleSuccessToast,
    // handleLoadingThenSuccessToast,
  } = useCanvasHandler();

  const handleGenerate = (e) => {
    e.preventDefault();
    showCanvasHandler(
      "Generate Salary",
      <UserProvider roleFilter="user_hr">
        <CreateSalaryPage
          onSuccess={(data) => {
            // Use the generateSalary function with the specified payload structure
            const payload = {
              month: data.month || new Date().getMonth() + 1,
              year: data.year || new Date().getFullYear(),
              users: data.users || ["9e9c22ce-09d6-4cf4-b255-79bd116cc7ab"],
              start_date: data.start_date,
              end_date: data.end_date,
              date_salary: data.date_salary || data.start_date,
              force_regenerate: data.force_regenerate,
              overwrite_existing: data.overwrite_existing,
            };

            generateSalary(payload)
              .then((response) => {
                if (response.success) {
                  const summary = getGenerateSalarySummary(response);
                  const periodLabel = summary.payrollStartDate && summary.payrollEndDate
                    ? `${formatPayrollPeriod({ startDate: summary.payrollStartDate, endDate: summary.payrollEndDate })}`
                    : formatPayrollPeriod({ startDate: payload.start_date, endDate: payload.end_date });

                  Swal.fire({
                    icon: "success",
                    title: "Salary generated successfully",
                    html: `
                      <div style="text-align:left">
                        <div><strong>Periode:</strong> ${periodLabel || "-"}</div>
                        <div><strong>Total staff:</strong> ${summary.rows.length}</div>
                        <hr style="margin:10px 0" />
                        <div><strong>Over DO:</strong> ${summary.overDoRows.length} staff / ${summary.totalOverDoDays} hari</div>
                        <div><strong>Potongan Over DO:</strong> ${formatCurrencyText(summary.totalOverDoDeduction)}</div>
                        <div><strong>Uang Makan Pagi:</strong> ${summary.totalMorningMealDays} hari / ${formatCurrencyText(summary.totalMorningMeal)}</div>
                        <div><strong>Total Gaji Bersih:</strong> ${formatCurrencyText(summary.totalSalary)}</div>
                      </div>
                    `,
                  });
                  handleSuccessToast(true, "Salary generated successfully");
                  refetch();
                } else {
                  throw new Error(
                    response.message || "Failed to generate salary"
                  );
                }
              })
              .catch((error) => {
                const message = error?.message || "Failed to generate salary";
                if (/already generated/i.test(message)) {
                  Swal.fire({
                    icon: "warning",
                    title: "Salary sudah pernah digenerate",
                    text: "Salah satu staff pada pilihan ini sudah punya salary di periode tersebut. Aktifkan opsi regenerate/overwrite, atau pilih hanya staff yang belum digenerate.",
                  });
                  return;
                }
                handleError(error);
              });
          }}
          onError={handleError}
        />
      </UserProvider>
    );
  };

  const handleViewDetails = async (id) => {
    try {
      Swal.fire({
        title: "Loading salary details...",
        text: "Mohon tunggu sebentar.",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      const response = await getRequest(`/ahr/salaries/${id}`);
      Swal.close();

      if (!response?.success || !response?.data) {
        throw new Error(response?.message || "Salary record not found");
      }

      showCanvasHandler(
        "Salary Details",
        <SalaryDetailPage
          salaryData={response.data}
          onClose={() => {
            // Close canvas functionality is handled by the canvas handler
          }}
        />
      );
    } catch (error) {
      Swal.close();
      handleError(error);
    }
  };

  const handleManageCategory = (salaryId, categoryType) => {
    const categoryTitles = {
      facility: "Facilities",
      insurance: "Insurances",
      deduction: "Deductions",
      work_bonus: "Work Bonuses",
      other: "Other Bonuses",
    };

    showCanvasHandler(
      `Manage ${categoryTitles[categoryType]}`,
      <SalaryDetailProvider>
        <SalaryDetailManagement
          salaryId={salaryId}
          categoryType={categoryType}
          onDataChanged={() => refetch()}
          onClose={() => {}}
        />
      </SalaryDetailProvider>
    );
  };

  const handleOpenStaffLoanDesign = () => {
    navigate(URLS.v1.HR_MANAGE_STAFF_LOAN);
  };

  const handleSendEmail = (salaryId = null) => {
    const isSingle = Boolean(salaryId);
    const targetSalaries = isSingle
      ? (salaryData || []).filter((salary) => salary.id === salaryId)
      : selectedSalaryIds.length > 0
        ? (salaryData || []).filter((salary) => selectedSalaryIds.includes(salary.id))
        : (salaryData || []);
    const targetSalaryIds = targetSalaries.map((salary) => salary.id);

    if (targetSalaryIds.length === 0) {
      Swal.fire("Data belum dipilih", "Pilih minimal 1 data salary untuk dikirim.", "warning");
      return;
    }

    showCanvasHandler(
      isSingle ? "Salary Email Settings" : "Batch Salary Email Settings",
      <SalaryEmailSettingsCanvas
        mode={isSingle ? "single" : "batch"}
        targetSalaryIds={targetSalaryIds}
        targetSalaries={targetSalaries}
        payrollPeriod={payrollPeriod}
        filterOptions={filterOptions}
      />
    );
  };
  
  const handleEditSalary = (salary) => {
    const salaryFormFields = [
      {
        name: "user_id",
        label: "Employee",
        type: "select",
        required: true,
        options: users.data?.map((u) => ({ value: u.id, label: u.name })),
      },
      {
        name: "date_salary",
        label: "Date Salary",
        type: "datetime-local",
        required: true,
      },
      {
        name: "basic_salary",
        label: "Basic Salary",
        type: "number",
        required: true,
        isPrice: true,
      },
      {
        name: "total_insurances",
        label: "Total Insurances",
        type: "number",
        isPrice: true,
      },
      {
        name: "total_facilities",
        label: "Total Facilities",
        type: "number",
        isPrice: true,
      },
      {
        name: "total_deductions",
        label: "Total Deductions",
        type: "number",
        isPrice: true,
      },
      {
        name: "total_work_bonuses",
        label: "Total Work Bonuses",
        type: "number",
        isPrice: true,
      },
      {
        name: "total_other",
        label: "Total Other",
        type: "number",
        isPrice: true,
      },
      {
        name: "total_salary",
        label: "Total Salary",
        type: "number",
        isPrice: true,
      },
      { name: "total_work", label: "Total Work (Days)", type: "number" },
      { name: "work_duration", label: "Work Duration (Hours)", type: "number" },
    ];

    showCanvasHandler(
      "Edit Salary",
      <MasterDataFormV2
        apiUrl="/ahr/salaries"
        initialData={salary}
        fields={salaryFormFields}
        onSuccess={() => {
          handleSuccessToast(true, "Salary updated successfully");
          refetch();
        }}
        onError={handleError}
      />
    );
  };

  const handleDeleteSalary = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        const response = await deleteSalary(id);
        if (response.success) {
          handleSuccessToast(true, "Salary deleted successfully");
          refetch();
        } else {
          handleError(new Error(response.message || "Failed to delete salary"));
        }
      } catch (error) {
        handleError(error);
      }
    }
  };

  const handleBulkDeleteSalary = async () => {
    if (selectedSalaryIds.length === 0) return;

    const result = await Swal.fire({
      title: `Delete ${selectedSalaryIds.length} salary data?`,
      text: "Data yang dipilih akan dihapus agar bisa digenerate ulang.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete selected",
    });

    if (!result.isConfirmed) return;

    try {
      Swal.fire({
        title: "Deleting selected salary data...",
        text: "Mohon tunggu sebentar.",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      const results = [];
      for (const id of selectedSalaryIds) {
        results.push(await deleteSalary(id));
      }
      const failed = results.filter((response) => !response?.success);
      if (failed.length > 0) {
        throw new Error(`${failed.length} salary data failed to delete`);
      }
      Swal.close();
      handleSuccessToast(true, `${selectedSalaryIds.length} salary data deleted successfully`);
      setSelectedSalaryIds([]);
      refetch();
    } catch (error) {
      Swal.close();
      handleError(error);
    }
  };


  const handleBulkPrintSlips = () => {
    if (selectedSalaryIds.length === 0 || !salaryData) return;

    const selectedSalaries = salaryData.filter(s => selectedSalaryIds.includes(s.id));
    if (selectedSalaries.length === 0) return;

    const printWindow = window.open("", "_blank");
    const pages = [];
    let currentPage = [];
    let currentSlots = 0;

    selectedSalaries.forEach((salary) => {
      const isLong = isSalarySlipLong(salary);
      const slotSize = isLong ? 2 : 1;

      if (currentSlots + slotSize > 4) {
        pages.push(currentPage);
        currentPage = [];
        currentSlots = 0;
      }

      currentPage.push({ salary, isLong });
      currentSlots += slotSize;
    });

    if (currentPage.length > 0) {
      pages.push(currentPage);
    }

    const htmls = pages.map((page) => `
      <div class="salary-slip-bulk-page">
        ${page.map(({ salary, isLong }) => getSalarySlipHtmlContent(salary, formatCurrency, {
          sizeClass: isLong ? "slip-quarter slip-quarter-wide" : "slip-quarter",
        })).join("")}
      </div>
    `);
    
    printWindow.document.write(`
      <html>
        <head>
          <title>Bulk Print Slip Gaji (${selectedSalaries.length} Slips)</title>
          ${getSalarySlipStyles()}
        </head>
        <body>
          ${htmls.join("")}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 500);
  };

  const handleBulkPrintSlipsV2 = () => {
    if (selectedSalaryIds.length === 0 || !salaryData) return;

    const selectedSalaries = salaryData.filter((salary) => selectedSalaryIds.includes(salary.id));
    if (selectedSalaries.length === 0) return;

    const pages = [];
    for (let index = 0; index < selectedSalaries.length; index += 4) {
      pages.push(selectedSalaries.slice(index, index + 4));
    }

    const htmls = pages.map((page) => `
      <div class="salary-slip-bulk-page">
        ${page.map((salary) => getSalarySlipHtmlContent(salary, formatCurrency, {
          sizeClass: "slip-quarter slip-quarter-v2",
        })).join("")}
      </div>
    `);

    const printWindow = window.open("", "_blank");
    printWindow.document.write(`
      <html>
        <head>
          <title>Bulk Print Slip Gaji V2 (${selectedSalaries.length} Slips)</title>
          ${getSalarySlipStylesV2()}
        </head>
        <body class="salary-slip-print-v2">
          ${htmls.join("")}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 500);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: "IDR",
      minimumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <LayoutHR>
      {getDevelopmentBanner()}
      <div className="salary-management-page">
        {/* Page Header */}
        <div className="page-header d-print-none mb-3">
          <div className="row align-items-center">
            <div className="col">
              <h2 className="page-title">Salary Management</h2>
              <div className="text-muted mt-1">
                Manage employee salaries and payroll
              </div>
            </div>
            <div className="col-auto">
              <div className="btn-list">
                <button
                  className="btn btn-outline-secondary"
                  onClick={() => navigate(URLS.v1.HR_SETTING_SALARY)}
                >
                  Salary Settings
                </button>
                <button
                  className="btn btn-outline-secondary"
                  onClick={handleOpenStaffLoanDesign}
                >
                  Staff Loan / Kasbon
                </button>
                <button
                  className="btn btn-outline-danger"
                  onClick={handleBulkDeleteSalary}
                  disabled={selectedSalaryIds.length === 0}
                >
                  Delete Selected
                </button>
                <button
                  className="btn btn-outline-primary"
                  onClick={handleBulkPrintSlips}
                  disabled={selectedSalaryIds.length === 0}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-printer" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                    <path d="M17 17h2a2 2 0 0 0 2 -2v-4a2 2 0 0 0 -2 -2h-14a2 2 0 0 0 -2 2v4a2 2 0 0 0 2 2h2" />
                    <path d="M17 9v-4a2 2 0 0 0 -2 -2h-6a2 2 0 0 0 -2 2v4" />
                    <path d="M7 13m0 2a2 2 0 0 1 2 -2h6a2 2 0 0 1 2 2v4a2 2 0 0 1 -2 2h-6a2 2 0 0 1 -2 -2z" />
                  </svg>
                   Print Selected
                  {selectedSalaryIds.length > 0 ? ` (${selectedSalaryIds.length})` : ""}
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleBulkPrintSlipsV2}
                  disabled={selectedSalaryIds.length === 0}
                  title="Versi font lebih besar, 4 slip per halaman A4"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-printer" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                    <path d="M17 17h2a2 2 0 0 0 2 -2v-4a2 2 0 0 0 -2 -2h-14a2 2 0 0 0 -2 2v4a2 2 0 0 0 2 2h2" />
                    <path d="M17 9v-4a2 2 0 0 0 -2 -2h-6a2 2 0 0 0 -2 2v4" />
                    <path d="M7 13m0 2a2 2 0 0 1 2 -2h6a2 2 0 0 1 2 2v4a2 2 0 0 1 -2 2h-6a2 2 0 0 1 -2 -2z" />
                  </svg>
                  Print Selected V2
                  {selectedSalaryIds.length > 0 ? ` (${selectedSalaryIds.length})` : ""}
                </button>
                <button className="btn btn-outline-info" onClick={() => handleSendEmail()} disabled={!filterOptions.filterMonth || !filterOptions.filterYear}>
                  <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-mail-forward" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                    <path d="M12 18h-7a2 2 0 0 1 -2 -2v-10a2 2 0 0 1 2 -2h14a2 2 0 0 1 2 2v7.5" />
                    <path d="M3 6l9 6l9 -6" />
                    <path d="M15 18h6" />
                    <path d="M18 15l3 3l-3 3" />
                  </svg>
                   Send Batch Email
                </button>
                <SalarySummaryReport
                  fetchAllSalaries={fetchAllSalaries}
                  filterOptions={filterOptions}
                  payrollPeriod={payrollPeriod}
                  formatCurrency={formatCurrency}
                  users={users.data || []}
                  isDisabled={!salaryData || salaryData.length === 0}
                />
                <button className="btn btn-primary" onClick={handleGenerate}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="icon icon-tabler icon-tabler-plus"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    strokeWidth="2"
                    stroke="currentColor"
                    fill="none"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                    <line x1="12" y1="5" x2="12" y2="19" />
                    <line x1="5" y1="12" x2="19" y2="12" />
                  </svg>
                  Generate Salary
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Development Status Banner */}
        <div className="alert alert-info py-2 mb-3 d-none" role="alert">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="icon icon-tabler icon-tabler-info-circle"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            strokeWidth="2"
            stroke="currentColor"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path stroke="none" d="M0 0h24v24H0z" fill="none" />
            <circle cx="12" cy="12" r="9" />
            <line x1="12" y1="8" x2="12.01" y2="8" />
            <polyline points="11,12 12,12 12,16 13,16" />
          </svg>
          Data payroll dihitung untuk periode <strong>tanggal 26 sampai 25 bulan berikutnya</strong>.
          {filterOptions.filterMonth && filterOptions.filterYear && payrollPeriod.startDate && (
            <> Periode aktif: <strong>{formatPayrollPeriod(payrollPeriod)}</strong>.</>
          )}
          <span className="text-muted ms-1 d-none">{DEVELOPMENT_STATUS.DATA_SOURCE.DUMMY_DATA}</span>
        </div>

        {/* Search and Filters */}
        <div className="card mb-3">
          <div className="card-body py-2">
            <div className="row g-2 align-items-center">
              <div className="col-md-3">
                <Select
                  classNamePrefix="react-select"
                  placeholder="All Departments"
                  isClearable
                  menuPlacement="auto"
                  menuPosition="fixed"
                  menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
                  options={departmentFilterOptions}
                  value={selectedDepartmentOption}
                  onChange={(option) => handleDepartmentFilterChange(option ? option.value : "")}
                  styles={{
                    control: (provided) => ({
                      ...provided,
                      minHeight: "38px",
                      borderRadius: "4px",
                    }),
                    menuPortal: (provided) => ({
                      ...provided,
                      zIndex: 3000,
                    }),
                    menu: (provided) => ({
                      ...provided,
                      zIndex: 3000,
                    }),
                  }}
                />
              </div>
              <div className="col-md-3">
                <Select
                  classNamePrefix="react-select"
                  placeholder="Pilih staff untuk lihat history..."
                  isClearable
                  menuPlacement="auto"
                  menuPosition="fixed"
                  menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
                  options={userFilterOptions}
                  value={selectedUserOption}
                  formatGroupLabel={(group) => (
                    <div className="d-flex align-items-center justify-content-between">
                      <span>{group.label}</span>
                      <span className="badge bg-blue-lt text-blue">{group.options.length}</span>
                    </div>
                  )}
                  onChange={(option) =>
                    handleUserFilterChange(option ? option.value : "")
                  }
                  styles={{
                    control: (provided) => ({
                      ...provided,
                      minHeight: "38px",
                      borderRadius: "4px",
                    }),
                    menuPortal: (provided) => ({
                      ...provided,
                      zIndex: 3000,
                    }),
                    menu: (provided) => ({
                      ...provided,
                      zIndex: 3000,
                    }),
                    groupHeading: (provided) => ({
                      ...provided,
                      color: "#155ca8",
                      fontSize: "11px",
                      fontWeight: 700,
                    }),
                  }}
                />
              </div>
              <div className="col-md-2">
                <label className="form-label small text-muted mb-1">Payroll Month</label>
                <select
                  className="form-select"
                  value={filterOptions.filterMonth}
                  onChange={(e) => handleMonthFilterChange(e.target.value)}
                >
                  <option value="">All Months</option>
                  {Array.from({ length: 12 }, (_, i) => i + 1).map((month) => (
                    <option key={month} value={month}>
                      {new Date(new Date().getFullYear(), month - 1, 1).toLocaleString("default", {
                        month: "long",
                      })}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-2">
                <label className="form-label small text-muted mb-1">Payroll Year</label>
                <select
                  className="form-select"
                  value={filterOptions.filterYear}
                  onChange={(e) => handleYearFilterChange(e.target.value)}
                >
                  <option value="">All Years</option>
                  {Array.from({ length: 6 }, (_, i) => new Date().getFullYear() - 5 + i).reverse().map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-2">
                <label className="form-label small text-muted mb-1 d-block">&nbsp;</label>
                <button
                  className="btn btn-outline-secondary w-100"
                  onClick={() => {
                    handleMonthFilterChange("");
                    handleYearFilterChange("");
                    handleDepartmentFilterChange("");
                    handleUserFilterChange("");
                  }}
                >
                  Clear Filters
                </button>
              </div>
            </div>
            <div className="text-muted small mt-1">
              Pilih staff lalu kosongkan bulan/tahun untuk melihat history salary staff tersebut lintas periode. Filter bulan menggunakan periode payroll 26-25.
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="card">
          <div className="card-body">
            <div className="table-responsive">
              <table className="table table-vcenter table-hover salary-management-table">
                <thead>
                  <tr>
                    <th className="text-center">
                      <input
                        className="form-check-input m-0"
                        type="checkbox"
                        checked={isAllVisibleSelected}
                        onChange={handleToggleSelectVisible}
                        aria-label="Select all salary data on this page"
                      />
                    </th>
                    <th>No</th>
                    <th>Action</th>
                    <th>Name</th>
                    <th>Rekening</th>
                    <th>Periode</th>
                    <th>Basic Salary</th>
                    <th>Facilities</th>
                    <th>Insurances</th>
                    <th>Work Bonus</th>
                    <th>Other Bonus</th>
                    <th>Deductions</th>
                    <th>Net Salary</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="13" className="text-center">
                        <div
                          className="spinner-border spinner-border-sm"
                          role="status"
                        >
                          <span className="visually-hidden">Loading...</span>
                        </div>
                        Loading salary records...
                      </td>
                    </tr>
                  ) : groupedSalaryData.length > 0 ? (
                    groupedSalaryData.map((group) => (
                      <React.Fragment key={group.key}>
                        <tr className="salary-period-row">
                          <td colSpan="13">
                            <span className="fw-bold">{group.label}</span>
                            <span className="text-muted ms-2">Periode {group.range}</span>
                            <span className="badge bg-blue-lt text-blue ms-2">{group.rowCount} staff</span>
                          </td>
                        </tr>
                        {group.departments.map((department) => (
                          <React.Fragment key={`${group.key}-${department.name}`}>
                            <tr className="salary-department-row">
                              <td colSpan="13">
                                <span className="fw-bold">{department.name}</span>
                                <span className="badge bg-azure-lt text-azure ms-2">{department.rows.length} staff</span>
                              </td>
                            </tr>
                            {department.rows.map((salary) => (
                      <tr key={salary.id}>
                        <td className="text-center">
                          <input
                            className="form-check-input m-0"
                            type="checkbox"
                            checked={selectedSalaryIds.includes(salary.id)}
                            onChange={() => handleToggleSalarySelection(salary.id)}
                            aria-label={`Select salary data ${salary.user?.name || salary.user_id}`}
                          />
                        </td>
                        <td>{salary.displayNumber}</td>
                        <td>
                          <div className="dropdown salary-action-dropdown">
                            <button
                              className="btn btn-sm btn-outline-secondary dropdown-toggle"
                              type="button"
                              data-bs-toggle="dropdown"
                              data-bs-boundary="viewport"
                              aria-expanded="false"
                            >
                              Actions
                            </button>
                            <ul className="dropdown-menu">
                              <li>
                                <button className="dropdown-item" onClick={() => handleViewDetails(salary.id)}>
                                  Salary Details
                                </button>
                              </li>
                              <li>
                                <button className="dropdown-item text-primary" onClick={() => handleSendEmail(salary.id)}>
                                  Send to Email
                                </button>
                              </li>
                              <li>
                                <button className="dropdown-item text-warning" onClick={() => handleEditSalary(salary)}>
                                  Edit Salary
                                </button>
                              </li>
                              <li>
                                <SalarySlipExport salary={salary} formatCurrency={formatCurrency} />
                              </li>
                              <li>
                                <hr className="dropdown-divider" />
                              </li>
                              <li>
                                <button className="dropdown-item text-info" onClick={() => handleManageCategory(salary.id, "facility")}>
                                  Facilities
                                </button>
                              </li>
                              <li>
                                <button className="dropdown-item text-warning" onClick={() => handleManageCategory(salary.id, "insurance")}>
                                  Insurances
                                </button>
                              </li>
                              <li>
                                <button className="dropdown-item text-primary" onClick={() => handleManageCategory(salary.id, "work_bonus")}>
                                  Work Bonus
                                </button>
                              </li>
                              <li>
                                <button className="dropdown-item text-secondary" onClick={() => handleManageCategory(salary.id, "other")}>
                                  Other Bonus
                                </button>
                              </li>
                              <li>
                                <button className="dropdown-item text-danger" onClick={() => handleManageCategory(salary.id, "deduction")}>
                                  Deductions
                                </button>
                              </li>
                              <li>
                                <hr className="dropdown-divider" />
                              </li>
                              <li>
                                <button className="dropdown-item text-danger" onClick={() => handleDeleteSalary(salary.id)}>
                                  Delete Salary
                                </button>
                              </li>
                            </ul>
                          </div>
                        </td>
                        <td className="fw-bold">
                          <button type="button" className="salary-name-link" onClick={() => handleViewDetails(salary.id)}>
                            {salary.user?.name || salary.user_id}
                          </button>
                        </td>
                        <td>{salary.user?.nomor_rekening || "-"}</td>
                        <td>
                          {getSalaryPeriod(salary.date_salary).range}
                        </td>
                        <td className="text-success">
                          {formatCurrency(salary.basic_salary)}
                        </td>
                        <td
                          className="text-info"
                          style={{
                            cursor: "pointer",
                            transition: "all 0.2s ease",
                            borderRadius: "4px",
                            padding: "8px",
                          }}
                          onClick={() =>
                            handleManageCategory(salary.id, "facility")
                          }
                          onMouseEnter={(e) =>
                            (e.target.style.backgroundColor =
                              "rgba(13, 110, 253, 0.1)")
                          }
                          onMouseLeave={(e) =>
                            (e.target.style.backgroundColor = "transparent")
                          }
                          title="Click to manage facilities"
                        >
                          {formatCurrency(getFacilityTotal(salary))}
                        </td>
                        <td
                          className="text-warning"
                          style={{
                            cursor: "pointer",
                            transition: "all 0.2s ease",
                            borderRadius: "4px",
                            padding: "8px",
                          }}
                          onClick={() =>
                            handleManageCategory(salary.id, "insurance")
                          }
                          onMouseEnter={(e) =>
                            (e.target.style.backgroundColor =
                              "rgba(255, 193, 7, 0.1)")
                          }
                          onMouseLeave={(e) =>
                            (e.target.style.backgroundColor = "transparent")
                          }
                          title="Click to manage insurances"
                        >
                          {formatCurrency(salary.total_insurances)}
                        </td>
                        <td
                          className="text-primary"
                          style={{
                            cursor: "pointer",
                            transition: "all 0.2s ease",
                            borderRadius: "4px",
                            padding: "8px",
                          }}
                          onClick={() =>
                            handleManageCategory(salary.id, "work_bonus")
                          }
                          onMouseEnter={(e) =>
                            (e.target.style.backgroundColor =
                              "rgba(0, 123, 255, 0.1)")
                          }
                          onMouseLeave={(e) =>
                            (e.target.style.backgroundColor = "transparent")
                          }
                          title="Click to manage work bonuses"
                        >
                          {formatCurrency(salary.total_work_bonuses)}
                        </td>
                        <td
                          className="text-secondary"
                          style={{
                            cursor: "pointer",
                            transition: "all 0.2s ease",
                            borderRadius: "4px",
                            padding: "8px",
                          }}
                          onClick={() =>
                            handleManageCategory(salary.id, "other")
                          }
                          onMouseEnter={(e) =>
                            (e.target.style.backgroundColor =
                              "rgba(108, 117, 125, 0.1)")
                          }
                          onMouseLeave={(e) =>
                            (e.target.style.backgroundColor = "transparent")
                          }
                          title="Click to manage other bonuses"
                        >
                          {formatCurrency(salary.total_other)}
                        </td>
                        <td
                          className="text-danger"
                          style={{
                            cursor: "pointer",
                            transition: "all 0.2s ease",
                            borderRadius: "4px",
                            padding: "8px",
                          }}
                          onClick={() =>
                            handleManageCategory(salary.id, "deduction")
                          }
                          onMouseEnter={(e) =>
                            (e.target.style.backgroundColor =
                              "rgba(220, 53, 69, 0.1)")
                          }
                          onMouseLeave={(e) =>
                            (e.target.style.backgroundColor = "transparent")
                          }
                          title="Click to manage deductions"
                        >
                          {formatCurrency(salary.total_deductions)}
                        </td>
                        <td className="fw-bold text-success">
                          {formatCurrency(salary.total_salary)}
                        </td>
                      </tr>
                            ))}
                          </React.Fragment>
                        ))}
                      </React.Fragment>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="13" className="text-center">
                        No salary records found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination & Limit Selector */}
            {pagination && (
              <div className="d-flex align-items-center justify-content-between p-3 border-top">
                <div className="d-flex align-items-center">
                  <span className="text-muted me-2 small">Rows per page:</span>
                  <select 
                    className="form-select form-select-sm w-auto" 
                    value={filterOptions.perPage} 
                    onChange={(e) => handlePerPageChange(Number(e.target.value))}
                  >
                    <option value="10">10</option>
                    <option value="50">50</option>
                    <option value="100">100</option>
                    <option value="200">200</option>
                    <option value="500">500</option>
                  </select>
                </div>
                <div>{Pagination}</div>
              </div>
            )}
          </div>
        </div>
      </div>
      <PrintJson data={salaries} />
    </LayoutHR>
  );
};

export default SalaryPage;
